import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Plus, Search } from 'lucide-react'
import { defekte as defekteApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { defektZustandBadge, defektStatusBadge } from '../../components/ui/Badge'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { formatDatum } from '../../lib/utils'
import { cn } from '../../lib/utils'

export function DefekteListePage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('alle')

  const { data, isLoading } = useQuery({
    queryKey: ['defekte', statusFilter],
    queryFn: () => defekteApi.list(statusFilter !== 'alle' ? { status: statusFilter } : {}),
  })

  const filtered = (data ?? []).filter((d) =>
    [d.beschreibung, d.kunde_name, d.geraet_modell, d.ursache].some((v) =>
      v?.toLowerCase().includes(search.toLowerCase())
    )
  )

  return (
    <>
      <TopBar
        title="Defekte"
        rightAction={<Button icon={<Plus size={18} />} size="sm" onClick={() => navigate('/defekte/neu')}>Melden</Button>}
      />
      <div className="px-4 py-3 space-y-3">
        <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} icon={<Search size={16} />} />
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {[
            { v: 'alle', l: 'Alle' },
            { v: 'offen', l: 'Offen' },
            { v: 'in-bearbeitung', l: 'In Bearbeitung' },
            { v: 'behoben', l: 'Behoben' },
          ].map(({ v, l }) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={cn('shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                statusFilter === v ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
              )}>
              {l}
            </button>
          ))}
        </div>

        {isLoading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="Keine Defekte"
            description="Defekte werden hier angezeigt"
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((d) => (
              <Card key={d.id} clickable onClick={() => navigate(`/defekte/${d.id}`)} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm text-gray-900 flex-1">{d.beschreibung}</p>
                  {defektStatusBadge(d.status)}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 font-medium">{d.kunde_name}</p>
                    {d.geraet_modell && <p className="text-xs text-gray-500">{d.geraet_modell} {d.seriennummer ? `· ${d.seriennummer}` : ''}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {defektZustandBadge(d.zustand)}
                    <span className="text-xs text-gray-400">{formatDatum(d.erstellt_am)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
